import { differenceInCalendarDays, isSameYear } from 'date-fns';
import {
  AlertTriangleIcon,
  CalendarClockIcon,
  FlameIcon,
  MessageSquareIcon,
  RotateCwIcon,
  SquareCheckIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useNow } from '@/shared/components/date';
import { EmptyState } from '@/shared/components/empty-state';
import { useFormatters } from '@/shared/i18n/format';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { SimpleTooltip } from '@/shared/ui/tooltip';

import type { TaskPriority, TaskStatus } from '../api';
import { STATUS_META } from '../model/status';

export function StatusBadge({ status, className }: { status: TaskStatus; className?: string }) {
  const { t } = useTranslation('tasks');
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <Badge variant={meta.badge} className={cn('gap-1', className)} data-status={status}>
      <Icon aria-hidden />
      {t(`status.${status}`)}
    </Badge>
  );
}

export function PriorityFlag({ priority, className }: { priority: TaskPriority; className?: string }) {
  const { t } = useTranslation('tasks');
  if (priority !== 'high') return null;
  return (
    <SimpleTooltip content={t('priority.high')}>
      <FlameIcon
        className={cn('size-3.5 shrink-0 text-destructive-text', className)}
        aria-label={t('priority.high')}
      />
    </SimpleTooltip>
  );
}

/** "#142" in a muted mono style. */
export function TaskNumber({ number, className }: { number: number; className?: string }) {
  return (
    <span className={cn('font-mono text-xs text-muted-foreground tabular-nums', className)}>#{number}</span>
  );
}

type DeadlineTone = 'overdue' | 'soon' | 'normal' | 'done';

function useDeadlineText(deadline: string | null) {
  const { t } = useTranslation('tasks');
  const fmt = useFormatters();
  const now = useNow();
  if (!deadline) return { text: t('deadline.none'), days: null as number | null };
  const date = new Date(deadline);
  const days = differenceInCalendarDays(date, now);
  const time = fmt.date(date, 'time');
  if (days === 0) return { text: t('deadline.todayAt', { time }), days };
  if (days === 1) return { text: t('deadline.tomorrowAt', { time }), days };
  if (days === -1) return { text: t('deadline.yesterdayAt', { time }), days };
  return { text: fmt.pattern(date, isSameYear(date, now) ? 'd MMM, HH:mm' : 'd MMM yyyy'), days };
}

/**
 * Deadline with urgency coloring: red when overdue, amber when due within a day, muted when closed or absent.
 * `compact` renders a pill (cards), otherwise inline text (tables, card fields).
 */
export function DeadlineText({
  deadline,
  isOverdue,
  closed = false,
  compact = false,
  className,
}: {
  deadline: string | null;
  isOverdue: boolean;
  closed?: boolean;
  compact?: boolean;
  className?: string;
}) {
  const fmt = useFormatters();
  const { text, days } = useDeadlineText(deadline);
  const tone: DeadlineTone = closed
    ? 'done'
    : isOverdue
      ? 'overdue'
      : days !== null && days <= 1
        ? 'soon'
        : 'normal';
  const title = deadline ? `${fmt.date(deadline, 'dateLong')}, ${fmt.date(deadline, 'time')}` : undefined;
  return (
    <span
      title={title}
      data-tone={tone}
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap tabular-nums',
        compact && 'rounded-md px-1.5 py-0.5 text-xs font-medium',
        !deadline && 'text-subtle-foreground',
        deadline &&
          tone === 'overdue' &&
          (compact ? 'bg-destructive-soft text-destructive-text' : 'font-medium text-destructive-text'),
        deadline && tone === 'soon' && (compact ? 'bg-warning-soft text-warning-text' : 'text-warning-text'),
        deadline && tone === 'normal' && (compact ? 'bg-muted text-muted-foreground' : 'text-foreground'),
        deadline && tone === 'done' && 'text-muted-foreground',
        className,
      )}
    >
      {compact && <CalendarClockIcon className="size-3.5" aria-hidden />}
      {text}
    </span>
  );
}

/** Small counters of a task row/card: checklist progress and comments (new ones highlighted). */
export function TaskMetrics({
  checklist,
  commentCount,
  newCommentCount,
  className,
}: {
  checklist: { done: number; total: number };
  commentCount: number;
  newCommentCount: number;
  className?: string;
}) {
  const { t } = useTranslation('tasks');
  if (checklist.total === 0 && commentCount === 0) return null;
  return (
    <span className={cn('inline-flex items-center gap-2.5 text-xs text-muted-foreground', className)}>
      {checklist.total > 0 && (
        <span
          className={cn(
            'inline-flex items-center gap-1 tabular-nums',
            checklist.done === checklist.total && 'text-success-text',
          )}
          aria-label={t('checklist.progress', { done: checklist.done, total: checklist.total })}
        >
          <SquareCheckIcon className="size-3.5" aria-hidden />
          {checklist.done}/{checklist.total}
        </span>
      )}
      {commentCount > 0 && (
        <span
          className={cn(
            'inline-flex items-center gap-1 tabular-nums',
            newCommentCount > 0 && 'rounded-full bg-brand px-1.5 font-semibold text-brand-foreground',
          )}
          aria-label={
            newCommentCount > 0
              ? t('comments.newCount', { count: newCommentCount })
              : t('comments.count', { count: commentCount })
          }
        >
          <MessageSquareIcon className="size-3.5" aria-hidden />
          {newCommentCount > 0 ? `+${newCommentCount}` : commentCount}
        </span>
      )}
    </span>
  );
}

/** The one "tasks could not be loaded" state of every task view (list, boards, plan, profile tab), with Retry. */
export function TaskLoadError({ onRetry, size = 'md' }: { onRetry: () => void; size?: 'sm' | 'md' }) {
  const { t } = useTranslation('tasks');
  return (
    <EmptyState
      icon={AlertTriangleIcon}
      size={size}
      title={t('list.loadError')}
      description={t('list.loadErrorHint')}
      action={
        <Button size="sm" variant="outline" onClick={onRetry}>
          <RotateCwIcon />
          {t('shared:actions.retry')}
        </Button>
      }
    />
  );
}
